/* Herlamora collections — tab switching, arrow scrolling and the thin
   custom scroll indicator for the herlamora-collections section. */

if (!customElements.get('herlamora-collections')) {
  customElements.define(
    'herlamora-collections',
    class HerlamoraCollections extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-panel]'));

        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.selectTab(index));
        });

        this.panels.forEach((panel) => this.setupPanel(panel));

        // The section stylesheet loads in the body, so sizes measured on
        // connect can be pre-CSS. The observer fires again once the layout
        // settles (and on any later resize) and corrects the indicator.
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => {
            this.panels.forEach((panel) => this.updatePanel(panel));
          });
          this.panels.forEach((panel) => {
            if (panel.hcScroller) this.resizeObserver.observe(panel.hcScroller);
          });
        } else {
          window.addEventListener('resize', () =>
            this.panels.forEach((panel) => this.updatePanel(panel))
          );
        }

        window.addEventListener('load', () =>
          this.panels.forEach((panel) => this.updatePanel(panel))
        );
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      selectTab(index) {
        this.tabs.forEach((tab, i) => {
          tab.classList.toggle('is-active', i === index);
          tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
        this.panels.forEach((panel, i) => {
          panel.hidden = i !== index;
        });
        // Sizes are 0 while hidden — recalculate once visible.
        this.updatePanel(this.panels[index]);
      }

      setupPanel(panel) {
        const scroller = panel.querySelector('[data-scroller]');
        if (!scroller) return;

        panel.hcScroller = scroller;
        panel.hcThumb = panel.querySelector('[data-thumb]');
        panel.hcIndicator =
          panel.querySelector('[data-indicator]') ||
          panel.querySelector('.herlamora-collections__indicator');
        panel.hcPrev = panel.querySelector('[data-prev]');
        panel.hcNext = panel.querySelector('[data-next]');

        scroller.addEventListener(
          'scroll',
          () => {
            if (panel.hcTick) return;
            panel.hcTick = true;
            requestAnimationFrame(() => {
              panel.hcTick = false;
              this.updatePanel(panel);
            });
          },
          { passive: true }
        );

        const step = (direction) => {
          // first VISIBLE card — the color-shop section hides its first
          // card on desktop (shown as the featured tile instead)
          const card = Array.from(scroller.querySelectorAll('.grid__item')).find(
            (item) => item.offsetWidth > 0
          );
          if (!card) return;
          const gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
          const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth';
          scroller.scrollBy({ left: direction * (card.offsetWidth + gap), behavior });
        };

        if (panel.hcPrev) panel.hcPrev.addEventListener('click', () => step(-1));
        if (panel.hcNext) panel.hcNext.addEventListener('click', () => step(1));

        this.updatePanel(panel);
      }

      updatePanel(panel) {
        const scroller = panel.hcScroller;
        if (!scroller || panel.hidden) return;

        const maxScroll = scroller.scrollWidth - scroller.clientWidth;

        if (panel.hcIndicator) {
          panel.hcIndicator.classList.toggle('is-hidden', maxScroll <= 0);
        }

        if (panel.hcThumb && panel.hcIndicator) {
          const track = panel.hcIndicator.clientWidth;
          const fraction = scroller.scrollWidth > 0 ? scroller.clientWidth / scroller.scrollWidth : 1;
          const thumbWidth = Math.min(track * fraction, track);
          const progress = maxScroll > 0 ? Math.min(scroller.scrollLeft / maxScroll, 1) : 0;
          panel.hcThumb.style.width = `${thumbWidth}px`;
          panel.hcThumb.style.transform = `translateX(${progress * (track - thumbWidth)}px)`;
        }

        const atStart = scroller.scrollLeft <= 1;
        const atEnd = scroller.scrollLeft >= maxScroll - 1;
        if (panel.hcPrev) panel.hcPrev.classList.toggle('is-disabled', atStart);
        if (panel.hcNext) panel.hcNext.classList.toggle('is-disabled', atEnd || maxScroll <= 0);
      }
    }
  );
}
